import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Site } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Button, Card, CheckboxField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { FileDrop } from '../../components/FileDrop';
import type { FileDropResult } from '../../components/FileDrop';
import { section, useFileUrl, useObjectUrl, useScopedSettings } from './api';

interface Look {
  welcomeImageFileId: string | null;
  logoFileId: string | null;
  backgroundColour: string;
  textColour: string;
  welcomeText: string;
  showClock: boolean;
}

/** A scaled-down copy of the kiosk welcome screen (4:3), following the unsaved values. */
export function KioskPreview({ look, imageUrl, logoUrl }: { look: Look; imageUrl: string | null; logoUrl: string | null }) {
  const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(new Date());
  return (
    <div
      role="img"
      aria-label={t('sitesUi.look.previewLabel')}
      className="relative aspect-[4/3] w-full overflow-hidden rounded-xl border border-slate-300"
      style={{
        backgroundColor: look.backgroundColour,
        color: look.textColour,
        backgroundImage: imageUrl ? `url("${imageUrl}")` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {logoUrl ? <img src={logoUrl} alt="" className="absolute left-4 top-4 h-10 max-w-[40%] object-contain" /> : null}
      {look.showClock ? <span className="absolute right-4 top-4 text-lg font-semibold">{time}</span> : null}
      <span className="absolute inset-x-4 bottom-8 text-center text-xl font-bold">{look.welcomeText || t('sitesUi.look.defaultWelcome')}</span>
    </div>
  );
}

/** Upload (and remove) for one of the site's images. */
function ImageCard({
  siteId,
  endpoint,
  label,
  hint,
  removeLabel,
  fileId,
  sizes,
  accept,
  maxBytes,
  canEdit,
  onChanged,
}: {
  siteId: string;
  endpoint: 'welcome-image' | 'logo';
  label: string;
  hint: string;
  removeLabel: string;
  fileId: string | null;
  sizes?: Parameters<typeof FileDrop>[0]['sizes'];
  accept: string[];
  maxBytes: number;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [chosen, setChosen] = useState<FileDropResult | null>(null);
  const url = useFileUrl(fileId);
  const chosenBlob = chosen?.blobs[0] ?? null;
  const previewUrl = useObjectUrl(chosenBlob && chosenBlob.type.startsWith('image/') ? chosenBlob : null);
  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      if (chosen && chosenBlob) form.append('file', chosenBlob, chosen.file.name);
      return api.post(`/sites/${siteId}/branding/${endpoint}`, { form });
    },
    onSuccess: () => {
      setChosen(null);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/sites/${siteId}/branding/${endpoint}`),
    onSuccess: onChanged,
  });
  return (
    <Card title={label} description={hint}>
      <div className="space-y-3">
        <ErrorLine error={upload.error ?? remove.error} />
        {previewUrl ? (
          <figure>
            <img src={previewUrl} alt={t('sitesUi.look.cropPreview')} className="max-h-48 rounded-lg border border-slate-200" />
            <figcaption className="mt-1 text-xs text-slate-600">{t('sitesUi.look.cropPreview')}</figcaption>
          </figure>
        ) : url ? (
          <img src={url} alt="" className="max-h-32 rounded-lg border border-slate-200 object-contain" />
        ) : null}
        {canEdit ? (
          <>
            <FileDrop label={label} accept={accept} maxBytes={maxBytes} sizes={sizes} onChange={setChosen} />
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" disabled={!chosen || upload.isPending} onClick={() => upload.mutate()}>
                {t('sitesUi.look.upload')}
              </Button>
              {fileId ? (
                <Button disabled={remove.isPending} onClick={() => remove.mutate()}>
                  {removeLabel}
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
}

export function KioskLookTab({ site, canEdit }: { site: Site; canEdit: boolean }) {
  const qc = useQueryClient();
  const base = `/sites/${site.id}/settings`;
  const { query, save, values } = useScopedSettings(base, 'sites');
  const stored = section<Partial<Look>>(values, 'kioskBranding');
  const [draft, setDraft] = useState<Partial<Look>>({});
  useEffect(() => setDraft({}), [values]);
  const look: Look = {
    welcomeImageFileId: null,
    logoFileId: null,
    backgroundColour: '#FFFFFF',
    textColour: '#0F172A',
    welcomeText: '',
    showClock: true,
    ...stored,
    ...draft,
  };
  const imageUrl = useFileUrl(look.welcomeImageFileId);
  const logoUrl = useFileUrl(look.logoFileId);

  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values) return <Spinner />;
  const keys = ['backgroundColour', 'textColour', 'welcomeText', 'showClock'] as const;
  const changed = keys.some((k) => k in draft && draft[k] !== stored[k]);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['settings', base, 'sites'] });
  const patch = <K extends keyof Look>(k: K, v: Look[K]) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
      <div className="space-y-6">
        <Card
          title={t('sitesUi.look.title')}
          footer={
            canEdit ? (
              <Button
                variant="primary"
                disabled={!changed || save.isPending}
                onClick={() =>
                  save.mutate({
                    kioskBranding: {
                      backgroundColour: look.backgroundColour,
                      textColour: look.textColour,
                      welcomeText: look.welcomeText,
                      showClock: look.showClock,
                    },
                  })
                }
              >
                {t('common.save')}
              </Button>
            ) : null
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <ErrorLine error={save.error} />
            </div>
            <TextField
              label={t('sitesUi.look.backgroundColour')}
              type="color"
              value={look.backgroundColour}
              disabled={!canEdit}
              onChange={(e) => patch('backgroundColour', e.target.value.toUpperCase())}
            />
            <TextField
              label={t('sitesUi.look.textColour')}
              type="color"
              value={look.textColour}
              disabled={!canEdit}
              onChange={(e) => patch('textColour', e.target.value.toUpperCase())}
            />
            <TextField
              label={t('sitesUi.look.welcomeText')}
              hint={t('sitesUi.look.welcomeTextHint')}
              maxLength={120}
              value={look.welcomeText}
              disabled={!canEdit}
              onChange={(e) => patch('welcomeText', e.target.value)}
              className="sm:col-span-2"
            />
            <CheckboxField
              label={t('sitesUi.look.showClock')}
              checked={look.showClock}
              disabled={!canEdit}
              onChange={(e) => patch('showClock', e.target.checked)}
              className="sm:col-span-2"
            />
          </div>
        </Card>
        <ImageCard
          siteId={site.id}
          endpoint="welcome-image"
          label={t('sitesUi.look.welcomeImage')}
          hint={t('sitesUi.look.welcomeImageHint')}
          removeLabel={t('sitesUi.look.removeImage')}
          fileId={look.welcomeImageFileId}
          sizes={[{ width: 2048, height: 1536, mode: 'cover', type: 'image/jpeg', quality: 0.85 }]}
          accept={['image/jpeg', 'image/png']}
          maxBytes={10 * 1024 * 1024}
          canEdit={canEdit}
          onChanged={refresh}
        />
        <ImageCard
          siteId={site.id}
          endpoint="logo"
          label={t('sitesUi.look.logo')}
          hint={t('sitesUi.look.logoHint')}
          removeLabel={t('sitesUi.look.removeLogo')}
          fileId={look.logoFileId}
          accept={['image/jpeg', 'image/png', 'image/svg+xml']}
          maxBytes={1024 * 1024}
          canEdit={canEdit}
          onChanged={refresh}
        />
      </div>
      <div className="lg:sticky lg:top-4 lg:self-start">
        <h2 className="mb-2 text-base font-semibold text-slate-900">{t('sitesUi.look.preview')}</h2>
        <KioskPreview look={look} imageUrl={imageUrl} logoUrl={logoUrl} />
      </div>
    </div>
  );
}
